<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import TicketCategoriesAPI from 'dashboard/api/ticketCategories';
import CategoryForm from './CategoryForm.vue';
import SubcategoryList from './SubcategoryList.vue';
import SettingsLayout from '../../SettingsLayout.vue';
import BaseSettingsHeader from '../../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';

const { t } = useI18n();

const categories = ref([]);
const loading = ref(true);
const showCategoryModal = ref(false);
const editingCategory = ref(null);
const expandedCategory = ref(null);

const fetchCategories = async () => {
  try {
    loading.value = true;
    const response = await TicketCategoriesAPI.get();
    categories.value = response.data;
  } catch (error) {
    useAlert(t('TICKET_CATEGORIES.FETCH_ERROR'));
  } finally {
    loading.value = false;
  }
};

const openNewCategory = () => {
  editingCategory.value = null;
  showCategoryModal.value = true;
};

const openEditCategory = category => {
  editingCategory.value = { ...category };
  showCategoryModal.value = true;
};

const handleCategorySaved = () => {
  showCategoryModal.value = false;
  editingCategory.value = null;
  fetchCategories();
};

const deleteCategory = async category => {
  if (
    !window.confirm(
      t('TICKET_CATEGORIES.DELETE_CONFIRM', { name: category.name })
    )
  ) {
    return;
  }

  try {
    await TicketCategoriesAPI.delete(category.id);
    useAlert(t('TICKET_CATEGORIES.DELETE_SUCCESS'));
    fetchCategories();
  } catch (error) {
    const message =
      error.response?.data?.error || t('TICKET_CATEGORIES.DELETE_ERROR');
    useAlert(message);
  }
};

const toggleExpand = categoryId => {
  expandedCategory.value =
    expandedCategory.value === categoryId ? null : categoryId;
};

onMounted(fetchCategories);
</script>

<template>
  <SettingsLayout
    :is-loading="loading"
    :loading-message="$t('TICKET_CATEGORIES.LOADING')"
    :no-records-found="!categories.length"
    :no-records-message="$t('TICKET_CATEGORIES.EMPTY')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('TICKET_CATEGORIES.TITLE')"
        :description="$t('TICKET_CATEGORIES.DESCRIPTION')"
        feature-name="ticket_categories"
      >
        <template #actions>
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('TICKET_CATEGORIES.NEW')"
            @click="openNewCategory"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="flex flex-col gap-2">
        <div
          v-for="category in categories"
          :key="category.id"
          class="border border-n-weak rounded-lg overflow-hidden"
        >
          <!-- Category Header -->
          <div
            class="flex items-center justify-between p-4 bg-n-solid-2 cursor-pointer hover:bg-n-alpha-1"
            @click="toggleExpand(category.id)"
          >
            <div class="flex items-center gap-3">
              <div
                class="w-3 h-3 rounded-full"
                :style="{ backgroundColor: category.color }"
              />
              <span class="font-mono text-sm text-n-slate-11">
                {{ category.code }}
              </span>
              <span class="font-medium text-n-slate-12">
                {{ category.name }}
              </span>
              <span
                class="text-xs text-n-slate-10 bg-n-solid-3 px-2 py-0.5 rounded"
              >
                {{ category.subcategories_count }}
                {{ $t('TICKET_CATEGORIES.SUBCATEGORIES_COUNT') }}
              </span>
            </div>
            <div class="flex items-center gap-2">
              <Button
                v-tooltip.top="$t('TICKET_CATEGORIES.FORM.EDIT')"
                icon="i-lucide-pen"
                slate
                xs
                faded
                @click.stop="openEditCategory(category)"
              />
              <Button
                v-tooltip.top="$t('TICKET_CATEGORIES.FORM.DELETE')"
                icon="i-lucide-trash-2"
                xs
                ruby
                faded
                @click.stop="deleteCategory(category)"
              />
              <fluent-icon
                :icon="
                  expandedCategory === category.id
                    ? 'chevron-up'
                    : 'chevron-down'
                "
                size="16"
                class="text-n-slate-11"
              />
            </div>
          </div>

          <!-- Subcategories -->
          <SubcategoryList
            v-if="expandedCategory === category.id"
            :category="category"
            @updated="fetchCategories"
          />
        </div>
      </div>
    </template>

    <!-- Category Modal -->
    <woot-modal
      v-model:show="showCategoryModal"
      :on-close="() => (showCategoryModal = false)"
    >
      <CategoryForm
        :category="editingCategory"
        @saved="handleCategorySaved"
        @cancel="showCategoryModal = false"
      />
    </woot-modal>
  </SettingsLayout>
</template>
